import {
  Alignment,
  Button,
  Menu,
  MenuItem,
  Popover,
  ProgressBar,
  Switch,
  Tooltip,
} from '@blueprintjs/core';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { showToast } from 'Components/Generic/BOSToasterUtil';
import ExportButton from 'Components/Generic/Buttons/ExportButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { SITE_PAGES } from 'Config';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { useGlobalState } from 'Data/context/GlobalState';
import { getUserManagingOrgIds } from 'Data/context/Organizations';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import {
  CHARGE_ALLOCATION_STATUS,
  getTransactionCategory,
} from 'Pages/Transactions/Enum';
import PropTypes from 'prop-types';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { canUserViewAllCores, formatMoney } from 'Util';
import { formatDate } from 'Util/dateUtil';
import {
  generatePDFReport,
  getTitleForPDF,
} from 'Util/pdfUtil/reportPrintHelper';
import OrgTableBody from './OrgTableBody';
import styles from './ReportTable.module.css';
import {
  getCellClassname,
  getCellKey,
  getGroupingRowClassname,
  getGroupingRowKey,
  getRowHeight,
} from './reportUtil';

const columnHelper = createColumnHelper();

const getValueAsMoney = (value) => {
  return (value && !isNaN(value) && `$${formatMoney(value)}`) || '-    ';
};

const getCoreButtonForChargeAllocation = (charge_allocation, maxWidth) => {
  return (
    charge_allocation?.charge_account?.organization?.id && (
      <OrgNameWithDrawerButton
        maxWidth={maxWidth || 800}
        orgId={charge_allocation.charge_account.organization.id}
      />
    )
  );
};

const getCoreTotalLabel = (charge_allocation) => {
  return (
    <>
      <span style={{ paddingRight: 6 }}>
        {getCoreButtonForChargeAllocation(charge_allocation, 176)}
      </span>
      Grand Total
    </>
  );
};

const getCoreCategoryElement = (charge_allocation, maxWidth) => {
  return (
    <>
      <span style={{ paddingRight: 6 }}>
        {getCoreButtonForChargeAllocation(charge_allocation, maxWidth)}
      </span>
      {getTransactionCategory(charge_allocation.category) || ''}
    </>
  );
};

const totalMap = new Map();
const budgetMap = new Map();

const defaultGrouping = ['org_id', 'category', 'activity', 'abr'];
const defaultColVisibility = {
  date_posted: false,
};

const ChargeAccountReportTable = ({ reportOptions, reportData }) => {
  const [globalState, updateGlobalState] = useGlobalState();
  const [isExporting, setIsExporting] = useState(false);

  const [showDetails, setShowDetails, showDetailsRef] = useStateWithRef(
    globalState.reportViewOptions &&
      'showDetails' in globalState.reportViewOptions
      ? globalState.reportViewOptions.showDetails
      : true,
  );

  const [grouping, setGrouping] = useState(defaultGrouping);
  const [expanded, setExpanded] = useState(true);
  const [shownDateField, setShownDateField] = useState('date_cleared');
  const [columnVisibility, setColumnVisibility] =
    useState(defaultColVisibility);

  useEffect(() => {
    return () => {
      updateGlobalState('reportViewOptions', {
        showDetails: showDetailsRef.current,
      });
    };
  }, []);

  useEffect(() => {
    setIsExporting(false);
    totalMap.clear();
    budgetMap.clear();
  }, [reportData]);

  useEffect(() => {
    setGrouping([
      ...defaultGrouping,
      ...(!showDetails ? ['expense_code'] : []),
    ]);
  }, [showDetails]);

  useEffect(() => {
    if (reportOptions?.dateField) {
      setShownDateField(reportOptions.dateField);
    }
  }, [reportOptions?.dateField]);

  useEffect(() => {
    let datesToHide = ['date_cleared', 'date_posted'].filter(
      (field) => field !== shownDateField,
    );
    setColumnVisibility(Object.fromEntries(datesToHide.map((k) => [k, false])));
  }, [shownDateField]);

  const toggleVisibleDate = () => {
    setShownDateField((prevState) =>
      prevState === 'date_cleared' ? 'date_posted' : 'date_cleared',
    );
  };

  let getToggleDateButton = (label) => {
    return (
      <Tooltip content={`Click to toggle date field displayed`}>
        <span className={styles.clickable_header} onClick={toggleVisibleDate}>
          {label}
        </span>
      </Tooltip>
    );
  };

  let grandTotals = reportData.grandTotals;
  let grandTotalAmount = grandTotals.total_amount;

  const columns = useMemo(
    () => [
      columnHelper.accessor('charge_account.organization.id', {
        id: 'org_id',
        header: 'Core',
        cell: (info) => getCoreCategoryElement(info.row.original),
        getText: (ca) =>
          `${ca.charge_account?.organization?.name} ${getTransactionCategory(
            ca.category,
          )}`,
      }),
      columnHelper.accessor('category', {
        id: 'category',
        header: 'Category',
        cell: (info) => getCoreCategoryElement(info.row.original),
        getText: (ca) =>
          `${ca.charge_account?.organization?.name} ${getTransactionCategory(
            ca.category,
          )}`,
      }),
      columnHelper.accessor('date_posted', {
        id: 'date_posted',
        header: ({ table }) =>
          table.getState().grouping.includes('expense_code')
            ? 'Activity'
            : getToggleDateButton('Entered'),
        enableGrouping: false,
        cell: (info) => formatDate(info.getValue()),
        getText: (ca) => formatDate(ca.date_posted),
      }),
      columnHelper.accessor('date_cleared', {
        id: 'date_cleared',
        header: ({ table }) =>
          table.getState().grouping.includes('expense_code')
            ? 'Activity'
            : getToggleDateButton('Cleared'),
        enableGrouping: false,
        cell: (info) => formatDate(info.getValue()),
        getText: (ca) => formatDate(ca.date_cleared),
      }),
      columnHelper.accessor('charge_account.code', {
        id: 'activity',
        header: 'Activity',
        cell: (info) => info.getValue(),
        getText: (ca) => ca.charge_account?.code,
      }),
      columnHelper.accessor('expense_code.abr_code_fk', {
        header: 'abr',
        id: 'abr',
        cell: (info) => info.getValue(),
        getText: (ca) => ca.expense_code?.abr_code_fk,
      }),
      columnHelper.accessor('expense_code.expense_code', {
        header: 'Account',
        id: 'expense_code',
        getText: (ca) => ca.expense_code?.expense_code,
      }),
      columnHelper.accessor('transaction.vendor_id', {
        header: 'Vendor',
        id: 'vendor',
        enableGrouping: false,
        width: '16%',
        aggregationFn: '',

        aggregatedCell: (info) => {
          return info.row.groupingColumnId === 'expense_code'
            ? info.row.original.expense_code?.expense_code
            : info.getValue();
        },
        cell: (info) => (
          <VendorNameWithDrawerButton vendorId={info.getValue()} />
        ),
        getText: (ca) => ca.transaction?.vendor?.name,
        getAggText: (row) => {
          return row.groupingColumnId === 'expense_code'
            ? row.original.expense_code?.expense_code
            : '';
        },
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        enableGrouping: false,
        width: '24%',
        cell: (info) => (
          <Link
            to={`${SITE_PAGES.TRANSACTION_DETAIL.to}/${info.row.original.parent_transaction_id}`}>
            {info.getValue()}
          </Link>
        ),
        getText: (ca) => ca.description,
        getAggText: (row) => {
          return row.groupingColumnId === 'org_id'
            ? `${row.original.charge_account?.organization?.name} Grand Total`
            : row.groupingColumnId === 'category'
            ? `${
                row.original.charge_account?.organization?.name
              } ${getTransactionCategory(row.original.category)}`
            : row.groupingColumnId === 'abr'
            ? row.original.expense_code?.abr_code?.description
            : row.groupingColumnId === 'expense_code'
            ? row.original.expense_code?.description
            : row.groupingValue;
        },
        aggregatedCell: (info) => {
          return info.row.groupingColumnId === 'org_id'
            ? flexRender(getCoreTotalLabel, info.row.original)
            : info.row.groupingColumnId === 'category'
            ? flexRender(
                (info) => getCoreCategoryElement(info.row.original, 206),
                info,
              )
            : info.row.groupingColumnId === 'abr'
            ? info.row.original.expense_code?.abr_code?.description
            : info.row.groupingColumnId === 'expense_code'
            ? info.row.original.expense_code?.description
            : info.row.groupingValue;
        },
        footer: 'Grand Total',
      }),
      columnHelper.accessor((ca) => `${ca.id}_budget`, {
        id: 'budget',
        header: 'Budget',
        enableGrouping: false,
        moneyCell: true,
        cell: () => '',
        getText: () => '',
        getAggText: (row) => {
          return (
            row.groupingColumnId !== 'expense_code' &&
            row.groupingColumnId !== 'org_id' &&
            getValueAsMoney(getTotalBudgetForRow(row)?.total_amount)
          );
        },
        aggregatedCell: (info) => {
          return (
            info.row.groupingColumnId !== 'expense_code' &&
            info.row.groupingColumnId !== 'org_id' &&
            getValueAsMoney(getTotalBudgetForRow(info.row)?.total_amount)
          );
        },
      }),
      columnHelper.accessor(
        (ca) => ca.status === CHARGE_ALLOCATION_STATUS.CLEARED.key && ca.amount,
        {
          header: 'Cleared',
          id: 'cleared',
          enableGrouping: false,
          moneyCell: true,
          cell: (info) => getValueAsMoney(info.getValue()),
          getText: (ca) =>
            ca.status === CHARGE_ALLOCATION_STATUS.CLEARED.key &&
            getValueAsMoney(ca.amount),
          getAggText: (row) =>
            getValueAsMoney(getTotalDataForRow(row)?.cleared_total),
          aggregatedCell: (info) => {
            return getValueAsMoney(getTotalDataForRow(info.row)?.cleared_total);
          },
          footer: () => {
            return getValueAsMoney(grandTotals?.cleared_total);
          },
        },
      ),
      columnHelper.accessor(
        (ca) => ca.status === CHARGE_ALLOCATION_STATUS.OPEN.key && ca.amount,
        {
          header: 'Open',
          id: 'open',
          enableGrouping: false,
          moneyCell: true,
          cell: (info) => getValueAsMoney(info.getValue()),
          getText: (ca) =>
            ca.status === CHARGE_ALLOCATION_STATUS.OPEN.key &&
            getValueAsMoney(ca.amount),
          getAggText: (row) =>
            getValueAsMoney(getTotalDataForRow(row)?.open_total),
          aggregatedCell: (info) => {
            return getValueAsMoney(getTotalDataForRow(info.row)?.open_total);
          },
          footer: () => {
            return getValueAsMoney(grandTotals?.open_total);
          },
        },
      ),
      columnHelper.accessor(
        (ca) => ca.status === CHARGE_ALLOCATION_STATUS.PENDING.key && ca.amount,
        {
          header: 'Pending',
          id: 'pending',
          enableGrouping: false,
          moneyCell: true,
          cell: (info) => getValueAsMoney(info.getValue()),
          getText: (ca) =>
            ca.status === CHARGE_ALLOCATION_STATUS.PENDING.key &&
            getValueAsMoney(ca.amount),
          getAggText: (row) =>
            getValueAsMoney(getTotalDataForRow(row)?.pending_total),
          aggregatedCell: (info) => {
            return getValueAsMoney(getTotalDataForRow(info.row)?.pending_total);
          },
          footer: () => {
            return getValueAsMoney(grandTotals?.pending_total);
          },
        },
      ),
      columnHelper.accessor('amount', {
        header: 'Total',
        id: 'total',
        enableGrouping: false,
        moneyCell: true,
        cell: '',
        aggregatedCell: (info) => {
          let totalAmount = getTotalDataForRow(info.row)?.total_amount || 0;
          return getValueAsMoney(totalAmount);
        },
        getText: () => '',
        getAggText: (row) => {
          let totalAmount = getTotalDataForRow(row)?.total_amount || 0;
          return getValueAsMoney(totalAmount);
        },
        footer: () => {
          return getValueAsMoney(grandTotalAmount);
        },
      }),
      columnHelper.accessor('', {
        header: 'Balance',
        id: 'balance',
        enableGrouping: false,
        moneyCell: true,
        cell: () => '',
        getText: () => '',
        getAggText: (row) => {
          if (
            row.groupingColumnId === 'org_id' ||
            row.groupingColumnId === 'expense_code'
          ) {
            return '';
          }
          let budgetAmount =
            (row.groupingColumnId !== 'expense_code' &&
              getTotalBudgetForRow(row)?.total_amount) ||
            0;
          let totalAmount = getTotalDataForRow(row)?.total_amount || 0;
          totalAmount = row.id.includes('category:REVENUE')
            ? -1 * totalAmount
            : totalAmount;
          let difference = +budgetAmount + +totalAmount;
          return getValueAsMoney(difference);
        },
        aggregatedCell: (info) => {
          if (
            info.row.groupingColumnId === 'org_id' ||
            info.row.groupingColumnId === 'expense_code'
          ) {
            return '';
          }
          let budgetAmount =
            (info.row.groupingColumnId !== 'expense_code' &&
              getTotalBudgetForRow(info.row)?.total_amount) ||
            0;
          let totalAmount = getTotalDataForRow(info.row)?.total_amount || 0;
          totalAmount = info.row.id.includes('category:REVENUE')
            ? -1 * totalAmount
            : totalAmount;
          let difference = +budgetAmount + +totalAmount;
          return getValueAsMoney(difference);
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: reportData.records,
    defaultColumn: {},
    columns,
    state: { grouping, columnVisibility, expanded },
    autoResetExpanded: false,
    onGroupingChange: setGrouping,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const [reportGroupsByOrg, setReportGroupsByOrg] = useState([]);

  useEffect(() => {
    let orgReportRows = table
      .getRowModel()
      .rows.filter((row) => row.depth === 0)
      .map((row, index) => {
        let orgRows = getRowsRecursively(row, index);

        // let totalHeightForGroup = orgRows.reduce((acc, rowInfo) => {
        //   return rowInfo.height ? acc + rowInfo.height : acc;
        // }, 0);

        let orgName =
          orgRows[0].row.original?.charge_account?.organization?.name || '';

        let orgRef = createRef();

        return { orgName, orgRows, orgRef };
      });
    setReportGroupsByOrg(orgReportRows);
  }, [table.getRowModel()]);

  const getRowsRecursively = (row, index, rowArray) => {
    if (!rowArray) {
      rowArray = [];
    }

    if (row.groupingColumnId === 'org_id') {
      row.subRows.forEach((subRow) =>
        getRowsRecursively(subRow, index, rowArray),
      );
      rowArray.push({
        row: row,
        index: 0,
        type: 'LEAF',
        markAsGroupEnd: true,
        height: getRowHeight(row),
      });
    } else if (row.groupingColumnId === 'expense_code') {
      rowArray.push({
        row: row,
        index: index,
        type: 'LEAF',
        height: getRowHeight(row),
      });
    } else if (row.depth === grouping.length - 1) {
      rowArray.push({
        row: row,
        index: index,
        type: 'GROUPING',
        height: getRowHeight(row, true),
      });
      row.getIsExpanded() &&
        row.subRows.forEach((subRow, index) => {
          rowArray.push({
            row: subRow,
            index: index,
            type: 'LEAF',
            height: getRowHeight(subRow),
          });
        });
      rowArray.push({
        row: row,
        index: 0,
        type: 'LEAF',
        height: getRowHeight(row),
      });
    } else {
      rowArray.push({
        row: row,
        index: index,
        type: 'GROUPING',
        height: getRowHeight(row, true),
      });
      row.getIsExpanded() &&
        row.subRows.forEach((subRow, index) => {
          getRowsRecursively(subRow, index, rowArray);
        });
      rowArray.push({
        row: row,
        index: 0,
        type: 'LEAF',
        height: getRowHeight(row),
      });
    }

    return rowArray;
  };

  const [pdfReportLines, setPdfReportLines] = useState([]);

  useEffect(() => {
    let reportRows = [];
    table
      .getRowModel()
      .rows.filter((row) => row.depth === 0)
      .forEach((row, index) => {
        addRowsForReport(reportRows, row, index);
      });
    setPdfReportLines(reportRows);
  }, [table.getRowModel(), columnVisibility]);

  const getTotalDataForRow = (row) => {
    let totalObject = totalMap.get(row?.id);
    let groupingId = row?.groupingColumnId;

    if (!totalObject) {
      let totalsList =
        groupingId === 'org_id'
          ? reportData.totalsByCore
          : groupingId === 'category'
          ? reportData.totalsByCategory
          : groupingId === 'activity'
          ? reportData.totalsByAccountCode
          : groupingId === 'abr'
          ? reportData.totalsByABR
          : groupingId === 'expense_code'
          ? reportData.totalsByEC
          : [];

      totalObject =
        totalsList && totalsList.find((to) => to.total_data_id === row.id);
      totalObject && totalMap.set(row?.id || grouping, totalObject);
    }

    return totalObject;
  };

  const getTotalBudgetForRow = (row) => {
    let totalObject = budgetMap.get(row?.id);
    let groupingId = row?.groupingColumnId;

    if (!totalObject) {
      let totalsList =
        groupingId === 'category'
          ? reportData.budgetsByCategory
          : groupingId === 'activity'
          ? reportData.budgetsByAccountCode
          : groupingId === 'abr'
          ? reportData.budgetsByABR
          : [];

      totalObject =
        totalsList && totalsList.find((to) => to.total_data_id === row.id);
      totalObject && budgetMap.set(row?.id || grouping, totalObject);
    }

    return totalObject;
  };

  let numVisibleCols = table.getVisibleLeafColumns().length - grouping.length;

  const getLeafRowForPDFReport = (row, index, markAsGroupEnd) => {
    let markAsGroup =
      markAsGroupEnd ||
      (row.id.includes('category:EXPENSE') && row.depth === 1);
    return row
      .getVisibleCells()
      .filter((cell) => !cell.column.getIsGrouped())
      .map((cell) => ({
        id: getCellKey(cell, markAsGroup ? 'END' : ''),
        className: getCellClassname(row, cell, index),
        text: cell.getIsAggregated()
          ? (cell.column.columnDef.getAggText &&
              cell.column.columnDef.getAggText(row)) ||
            ''
          : (!cell.getIsPlaceholder() &&
              cell.column.columnDef.getText &&
              cell.column.columnDef.getText(row.original)) ||
            '',
      }));
  };
  const getGroupingRowForPDFReport = (row, index) => {
    let isGroupStart = row.depth === 1 && !(index === 0 && row.index === 0);

    return row.groupingColumnId === 'abr'
      ? [
          {
            id: getGroupingRowKey(row),
            className: styles.abr_header,
          },
          ...row
            .getVisibleCells()
            .filter((cell) => cell.column.columnDef.id === 'abr')
            .map((cell) => ({
              id: getCellKey(cell, 'HEADER'),
              key: getCellKey(cell, 'HEADER'),
              className: styles.abr_header,
              text:
                (cell.column.columnDef.getText &&
                  cell.column.columnDef.getText(row.original)) ||
                '',
            })),
          {
            id: getGroupingRowKey(row),
            className: styles.abr_header,
            colSpan: numVisibleCols - 2,
            text: row.original.expense_code?.abr_code?.description,
          },
        ]
      : row
          .getVisibleCells()
          .filter((cell) => cell.column.columnDef.id === row.groupingColumnId)
          .map((cell) => ({
            id: getCellKey(cell, isGroupStart ? 'BEGIN' : ''),
            key: cell.id,
            className: getGroupingRowClassname(row),
            text:
              row.groupingColumnId === 'activity'
                ? row.original.charge_account?.code
                : (cell.column.columnDef.getText &&
                    cell.column.columnDef.getText(row.original)) ||
                  '',
            colSpan: numVisibleCols,
          }));
  };
  const addRowsForReport = (rowArray, row, index) => {
    if (row.groupingColumnId === 'org_id') {
      row.subRows.forEach((subRow) =>
        addRowsForReport(rowArray, subRow, index),
      );
      rowArray.push(getLeafRowForPDFReport(row, 0, true));
    } else if (row.groupingColumnId === 'expense_code') {
      rowArray.push(getLeafRowForPDFReport(row, index));
    } else if (row.depth === grouping.length - 1) {
      rowArray.push(getGroupingRowForPDFReport(row, index));
      row.subRows.forEach((subRow, index) =>
        rowArray.push(getLeafRowForPDFReport(subRow, index)),
      );
      rowArray.push(getLeafRowForPDFReport(row, 0));
    } else {
      rowArray.push(getGroupingRowForPDFReport(row, index));
      row.subRows.forEach((subRow, index) =>
        addRowsForReport(rowArray, subRow, index),
      );
      rowArray.push(getLeafRowForPDFReport(row, 0));
    }
  };

  let callback = () => setIsExporting(false);
  let errorhandler = (e) => {
    console.error(`Error generating report for options: ${reportOptions}`);
    console.error(e);
    setIsExporting(false);
    showToast(`Error generating PDF: ${e.message}`, 'warning', 'issue');
  };

  const exportPdfClicked = (download = false) => {
    if (!pdfReportLines) {
      showToast(`No data for report.`, 'warning', 'issue');
      return;
    }

    setIsExporting(true);

    let title = getTitleForPDF(reportOptions, showDetails);
    generatePDFReport(title, pdfReportLines, download, callback, errorhandler);
  };

  let printReportButton = reportData?.records?.length > 0 && (
    <Tooltip content={'Download as PDF'}>
      <Button
        minimal
        className={styles.export_button}
        onClick={() => exportPdfClicked(true)}
        icon={'import'}
        disabled={isExporting}>
        PDF
      </Button>
    </Tooltip>
  );

  let openReportButton = reportData?.records?.length > 0 && (
    <Tooltip content={'Save as PDF'}>
      <Button
        minimal
        className={styles.export_button}
        onClick={() => exportPdfClicked(false)}
        icon={'document-open'}
        disabled={isExporting}>
        PDF
      </Button>
    </Tooltip>
  );

  const exportDataFunc = () => {
    let organizations;
    if (reportOptions?.organizations?.length > 0) {
      organizations = reportOptions.organizations.map((org) => org.id);
    } else if (!canUserViewAllCores()) {
      organizations = getUserManagingOrgIds();
    }

    ChargeAllocationData.exportReportAsCSV(
      {
        fileName: getTitleForPDF(reportOptions, showDetails),
        ...(organizations && {
          organizations,
        }),
      },
      { records: reportData.records },
    );
  };

  let exportButton = reportData?.records?.length > 0 && (
    <ExportButton exportDataFunc={exportDataFunc} />
  );

  let typeSelectComponent = (
    <Switch
      labelElement={<span>Show Details</span>}
      checked={showDetails}
      alignIndicator={Alignment.RIGHT}
      inline
      onChange={(e) => setShowDetails(e.target.checked)}
    />
  );

  const reportBoxRef = useRef();

  const scrollToOrg = (orgInfoObject) => {
    let { orgRef } = orgInfoObject;
    if (orgRef?.current) {
      let scrollToPosition = orgRef.current.offsetTop - 32;
      reportBoxRef.current.scrollTo({
        top: scrollToPosition,
        // behavior: 'smooth',
      });
    }
  };

  let jumpToMenu = (
    <Menu className={styles.jump_to_menu}>
      {reportGroupsByOrg.map((orgReportInfoObject, index) => {
        let { orgName } = orgReportInfoObject;
        return (
          <MenuItem
            key={index}
            onClick={() => scrollToOrg(orgReportInfoObject)}
            text={orgName}
          />
        );
      })}
    </Menu>
  );

  let jumpToButton = reportGroupsByOrg?.length > 1 && (
    <Popover
      minimal
      placement='bottom-start'
      content={jumpToMenu}
      inheritDarkTheme={false}>
      <Button
        className={styles.expand_all_button}
        icon={'circle-arrow-right'}
        text={'Jump to...'}
        onMouseDown={(e) => e.preventDefault()}
      />
    </Popover>
  );

  return (
    <>
      {isExporting && <ProgressBar />}

      <div className={styles.table_header}>
        <div style={{ marginRight: 'auto' }}>
          <Button
            className={styles.expand_all_button}
            icon={
              table.getIsAllRowsExpanded()
                ? 'double-chevron-up'
                : 'double-chevron-down'
            }
            onClick={table.getToggleAllRowsExpandedHandler()}>
            {table.getIsAllRowsExpanded() ? 'Collapse All' : 'Expand All'}
          </Button>
          {jumpToButton}
        </div>
        {typeSelectComponent}
        {exportButton}
        {openReportButton}
        {printReportButton}
      </div>

      <div
        id={'report_box'}
        ref={reportBoxRef}
        className={styles.report_table_container}>
        <table className={styles.report_table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <tr key={`${headerGroup.id}_${index}`}>
                {headerGroup.headers.map(
                  (header, index) =>
                    !header.column.getIsGrouped() && (
                      <th
                        key={`${header.id}_${index}`}
                        colSpan={header.colSpan}
                        className={
                          header.column.columnDef.moneyCell
                            ? styles.money_header
                            : ''
                        }
                        style={{
                          width: header.column.columnDef.width || '10%',
                        }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ),
                )}
              </tr>
            ))}
          </thead>

          {reportGroupsByOrg.map((orgReportInfoObject, index) => {
            return (
              <OrgTableBody
                key={index}
                orgReportInfoObject={orgReportInfoObject}
                index={index}
                numVisibleCols={numVisibleCols}
              />
            );
          })}

          {(!reportOptions?.organizations?.length ||
            reportOptions?.organizations?.length > 1) && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map(
                    (header) =>
                      !header.column.getIsGrouped() && (
                        <td
                          key={header.id}
                          className={`${styles.grand_total}${
                            header.column.columnDef.moneyCell
                              ? ' ' + styles.money_cell
                              : ''
                          }`}
                          colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.footer,
                                header.getContext(),
                              )}
                        </td>
                      ),
                  )}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      <div className={styles.record_number_label}>
        {'Total Records: ' + reportData?.count}
      </div>
    </>
  );
};

ChargeAccountReportTable.propTypes = {
  reportData: PropTypes.shape({
    count: PropTypes.number,
    records: PropTypes.array,
    budgetsByABR: PropTypes.array,
    budgetsByAccountCode: PropTypes.array,
    budgetsByCategory: PropTypes.array,
    totalsByEC: PropTypes.array,
    totalsByABR: PropTypes.array,
    totalsByAccountCode: PropTypes.array,
    totalsByCategory: PropTypes.array,
    totalsByCore: PropTypes.array,
    grandTotals: PropTypes.object,
  }),
  reportOptions: PropTypes.object,
};

export default ChargeAccountReportTable;
